import React, { useContext, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Row,
  Table,
} from 'reactstrap';
import { ApiContext } from '../../contexts/ApiContext';
import { useNotification } from '../../contexts/NotificationContext';
import CouponService from '../../service/coupon.service';
import { formatCentsToBRL } from '../../utils/currency';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal/ConfirmDeleteModal';
import CouponForm from './CouponForm';

const formatDate = (value) => {
  if (!value) return 'Sem validade';
  return new Date(value).toLocaleDateString('pt-BR');
};

const couponStatus = (coupon) => {
  if (!coupon.active) return { label: 'Inativo', color: 'secondary' };
  if (coupon.expiresAt && new Date(coupon.expiresAt).getTime() < Date.now()) {
    return { label: 'Expirado', color: 'warning' };
  }
  if (coupon.maxUses != null && coupon.usedCount >= coupon.maxUses) {
    return { label: 'Esgotado', color: 'warning' };
  }
  return { label: 'Ativo', color: 'success' };
};

const CouponsSettings = () => {
  const { account } = useContext(ApiContext);
  const { notify } = useNotification();
  const accountId = account?.id;
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const data = await CouponService.list(accountId);
      setCoupons(data || []);
    } catch (err) {
      console.error(err);
      notify('tr', 'danger', 'Erro ao carregar cupons', 'icon-alert-circle-exc');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const openCreate = () => {
    setEditing(null);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setEditing(null);
    setShowForm(false);
    load();
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await CouponService.remove(toDelete.id, accountId);
      notify('tr', 'success', 'Cupom excluído', 'icon-check-2');
      setToDelete(null);
      load();
    } catch (err) {
      notify('tr', 'danger', 'Não foi possível excluir o cupom', 'icon-alert-circle-exc');
    }
  };

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: 12 }}>
              <div>
                <CardTitle tag="h4">Cupons de desconto</CardTitle>
                <p className="card-category mb-0">
                  Crie códigos para o cliente usar no checkout. O desconto vale nos itens, não no frete.
                </p>
              </div>
              <Button color="primary" className="btn-fill" onClick={openCreate}>
                Novo cupom
              </Button>
            </CardHeader>
            <CardBody>
              {loading && <div>Carregando...</div>}
              {!loading && coupons.length === 0 && (
                <div className="text-center py-5">
                  <p className="text-muted mb-3" style={{ fontSize: 15 }}>
                    Nenhum cupom ainda. Crie um código para o cliente usar no checkout.
                  </p>
                  <Button color="primary" className="btn-fill" onClick={openCreate}>
                    Novo cupom
                  </Button>
                </div>
              )}
              {!loading && coupons.length > 0 && (
                <Table className="tablesorter" responsive>
                  <thead className="text-primary">
                    <tr>
                      <th>Código</th>
                      <th>Desconto</th>
                      <th>Usos</th>
                      <th>Validade</th>
                      <th>Status</th>
                      <th className="text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coupons.map((coupon) => {
                      const status = couponStatus(coupon);
                      const usageLabel =
                        coupon.maxUses != null
                          ? `${coupon.usedCount} / ${coupon.maxUses}`
                          : `${coupon.usedCount} · ilimitado`;
                      const usagePct =
                        coupon.maxUses != null
                          ? Math.min(100, Math.round((coupon.usedCount / coupon.maxUses) * 100))
                          : 0;
                      return (
                        <tr key={coupon.id}>
                          <td>
                            <span style={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: 0.6 }}>
                              {coupon.code}
                            </span>
                            {coupon.minSubtotalCents > 0 && (
                              <div className="text-muted" style={{ fontSize: 11 }}>
                                Mín. {formatCentsToBRL(coupon.minSubtotalCents)}
                              </div>
                            )}
                          </td>
                          <td>
                            <Badge color="info" pill>
                              {coupon.type === 'PERCENTAGE'
                                ? `${coupon.value}%`
                                : formatCentsToBRL(coupon.value)}
                            </Badge>
                          </td>
                          <td style={{ minWidth: 120 }}>
                            <div style={{ fontSize: 13 }}>{usageLabel}</div>
                            {coupon.maxUses != null && (
                              <div
                                className="mt-1"
                                style={{
                                  height: 4,
                                  borderRadius: 4,
                                  background: 'rgba(255,255,255,0.08)',
                                  overflow: 'hidden',
                                }}
                              >
                                <div
                                  style={{
                                    width: `${usagePct}%`,
                                    height: '100%',
                                    background: usagePct >= 100 ? '#ff8d72' : '#1d8cf8',
                                  }}
                                />
                              </div>
                            )}
                          </td>
                          <td>{formatDate(coupon.expiresAt)}</td>
                          <td>
                            <Badge color={status.color} pill>
                              {status.label}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <Button
                              className="btn-fill"
                              color="info"
                              size="sm"
                              title="Editar"
                              onClick={() => {
                                setEditing(coupon);
                                setShowForm(true);
                              }}
                            >
                              <i className="tim-icons icon-pencil" />
                            </Button>{' '}
                            <Button
                              className="btn-fill"
                              color="danger"
                              size="sm"
                              title="Excluir"
                              onClick={() => setToDelete(coupon)}
                            >
                              <i className="tim-icons icon-trash-simple" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <CouponForm
        coupon={editing}
        accountId={accountId}
        show={showForm}
        setShow={setShowForm}
        onClose={handleFormClose}
      />
      <ConfirmDeleteModal
        isOpen={!!toDelete}
        toggle={() => setToDelete(null)}
        message={
          toDelete
            ? `Deseja realmente excluir o cupom "${toDelete.code}"?`
            : 'Deseja realmente excluir?'
        }
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default CouponsSettings;
